import { Component, OnInit, ChangeDetectorRef, ElementRef, NgZone, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { forkJoin } from 'rxjs';
import _ from 'lodash';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Submission, Review } from 'src/app/model/paper';
import { Event as JemsEvent } from 'src/app/model/event';
import { SelectOption } from 'src/app/model/select.option';
import { SubmissionsFilter } from 'src/app/enum/submissions.filter';
import { AdminService } from 'src/app/service/admin.service';
import { SubmissionsService } from 'src/app/service/submissions.service';
import { EventsService } from 'src/app/service/events.service';
import { ExportService } from 'src/app/service/export.service';
import { SubmissionExportService } from 'src/app/service/submission-export.service';
import { replaceMatIconsWithSvg, stripAngularDevAttrs } from 'src/app/utils/ms-icons';

/**
 * `ExportSubmissionsChooserComponent` — página anterior ao show-all onde o
 * usuário escolhe o MODO de exportação. Ganhará filtros de submissões
 * (status, trilha, tópicos, etc.) num próximo passo.
 *
 * Modos:
 *   • single → navega pra `/admin/{event}/submissions/show-all?mode=single`
 *     (preview + download HTML único).
 *   • site   → NÃO navega. Faz o pipeline aqui mesmo (fetch + render offscreen
 *     + zip via `SubmissionExportService.buildSiteZipBlob`) mostrando overlay
 *     com spinner enquanto processa. Evita a "página cinza" intermediária.
 */
@Component({
  selector: 'app-export-submissions-chooser',
  templateUrl: './export-submissions-chooser.component.html',
  styleUrls: ['./export-submissions-chooser.component.scss'],
})
export class ExportSubmissionsChooserComponent implements OnInit, OnDestroy {
  public eventId: number;
  public event: JemsEvent | null = null;
  // Filtros: reusa <app-submissions-field-filter-v2 section="filters">
  // (mesmo componente da Lista de submissões) pra ter idêntico visual +
  // comportamento (status/tópicos/trilhas/grupos/TPC groups). Nomes dos
  // controls seguem o enum SubmissionsFilter.
  public filterForm: FormGroup;
  public paperGroupOptions: SelectOption[] = [];
  public readonly SubmissionsFilter = SubmissionsFilter;
  // Estado do overlay de "gerando .zip".
  public generating = false;
  public generatingLabel = '';
  // Batch renderizado offscreen (via `<app-submission-export-view *ngFor>`).
  public batch: Submission[] = [];
  private all: Submission[] = [];
  private metaMap = new Map<number, string>();
  private results = new Map<number, SafeHtml>();
  private destroyed = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private adminService: AdminService,
    private submissionsService: SubmissionsService,
    private eventsService: EventsService,
    private exportService: ExportService,
    private exportPipeline: SubmissionExportService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private hostElRef: ElementRef,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.eventId = +(this.route.parent?.snapshot.paramMap.get('eventId') || 0);
    // FormGroup com os controls que o <app-submissions-field-filter-v2> espera
    // (nomes seguem o enum SubmissionsFilter). Vazios = sem filtro.
    this.filterForm = this.fb.group({
      [SubmissionsFilter.STATUS]: [[]],
      [SubmissionsFilter.TOPICS]: [[]],
      [SubmissionsFilter.TRACKS]: [[]],
      [SubmissionsFilter.SUBMISSION_GROUPS]: [[]],
      [SubmissionsFilter.TPC_GROUPS]: [[]],
    });
    // Event p/ o filter-v2 (usa event.topics, event.tracks, event.id p/ TPC).
    this.event = this.adminService.selectedEvent;
    if (!this.event) {
      this.adminService.getEvent().subscribe(e => this.event = e);
    }
    // Paper groups (o filter-v2 recebe via @Input [groups]).
    if (this.eventId) {
      this.eventsService.getEventPaperGroups(this.eventId).subscribe(
        gs => this.paperGroupOptions = (gs || []).map(g => new SelectOption(g.id, g.name)),
        () => {});
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.adminService.progressBar?.stop();
  }

  public choose(mode: 'single' | 'site'): void {
    if (mode === 'single') {
      // Modo single mantém o fluxo antigo: navega pro show-all (preview).
      // Passa filtros via query params (arrays serializados como CSV).
      this.router.navigate(
        [`/admin/${this.eventId}/submissions/show-all`],
        { queryParams: { mode, ...this.filterQueryParams() } },
      );
      return;
    }
    // Modo site: fica aqui, mostra overlay, faz tudo inline.
    this.startSiteExport();
  }

  /** Lê valor do form filter por control name (do enum SubmissionsFilter). */
  private fv(name: string): any[] { return this.filterForm?.get(name)?.value || []; }

  /** Serializa filtros pra query params (CSV de ids). Omite chaves vazias. */
  private filterQueryParams(): Record<string, string> {
    const out: Record<string, string> = {};
    const s = this.fv(SubmissionsFilter.STATUS);
    const t = this.fv(SubmissionsFilter.TOPICS);
    const tr = this.fv(SubmissionsFilter.TRACKS);
    const pg = this.fv(SubmissionsFilter.SUBMISSION_GROUPS);
    const tg = this.fv(SubmissionsFilter.TPC_GROUPS);
    if (s.length) out.status = s.join(',');
    if (t.length) out.topics = t.join(',');
    if (tr.length) out.tracks = tr.join(',');
    if (pg.length) out.paperGroup = pg.join(',');
    if (tg.length) out.tpcGroup = tg.join(',');
    return out;
  }

  /** Aplica filtros in-memory ao array de submissões. */
  private applyFilters(subs: Submission[]): Submission[] {
    const st: string[] = this.fv(SubmissionsFilter.STATUS);
    const tp: number[] = this.fv(SubmissionsFilter.TOPICS);
    const tr: number[] = this.fv(SubmissionsFilter.TRACKS);
    const pg: number[] = this.fv(SubmissionsFilter.SUBMISSION_GROUPS);
    const tg: number[] = this.fv(SubmissionsFilter.TPC_GROUPS);
    return subs.filter(s => {
      if (st.length && !st.includes(s.status as any)) return false;
      if (tr.length && !tr.includes(+((s as any).track?.id ?? (s as any).track))) return false;
      if (pg.length && !pg.includes(+((s as any).paperGroup))) return false;
      if (tg.length && !tg.includes(+((s as any).tpcGroup))) return false;
      if (tp.length) {
        const subTopics = ((s as any).topics || []).map((t: any) => +(t?.id ?? t));
        if (!subTopics.some(id => tp.includes(id))) return false;
      }
      return true;
    });
  }

  metaOf(id: number): string { return this.metaMap.get(id) || ''; }
  trackById(_i: number, s: Submission): number { return s.id; }
  /** "ACRONYM YEAR" (ou só ACRONYM) — mesmo estilo da barra do site .zip. */
  eventLabel(): string {
    const acr = this.event?.acronym || '';
    const yr = (this.event as any)?.year;
    return yr ? `${acr} ${yr}` : acr;
  }

  private startSiteExport(): void {
    this.generating = true;
    this.generatingLabel = 'Carregando dados...';
    this.adminService.progressBar?.start();
    this.event = this.adminService.selectedEvent;
    if (this.event) { this.loadAndRender(); }
    else { this.adminService.getEvent().subscribe(e => { this.event = e; this.loadAndRender(); }); }
  }

  private loadAndRender(): void {
    forkJoin([
      this.submissionsService.getSubmissionsByEventFull(this.event.id, { detailed: true }),
      this.eventsService.getFormReview(this.event.id),
      this.eventsService.getFormSubmission(this.event.id),
    ]).subscribe(([rawSubs, reviewForm, submissionForm]: [Submission[], any, any]) => {
      const submissions = this.applyFilters(rawSubs);
      if (!submissions.length) {
        this.zone.run(() => {
          this.generating = false;
          this.adminService.progressBar?.stop();
          this.cdr.detectChanges();
        });
        return;
      }
      submissions.forEach(sub => {
        (sub as any).event = this.event;
        if (submissionForm) { (sub as any).formSubmission = submissionForm; }
        (sub.reviews || []).forEach((r: any) => {
          if (r instanceof Review && reviewForm) {
            r.form = _.cloneDeep(reviewForm);
            r.form.fields = r.form.fields.map((f: any) => {
              f.answers = r.answers?.filter((a: any) => a?.formField === f.id) || [];
              return f;
            });
          }
        });
        this.all.push(sub);
        this.metaMap.set(sub.id, (sub.track as any)?.name ? `Trilha: ${(sub.track as any).name}` : '');
      });
      this.generatingLabel = `Renderizando ${submissions.length} submissões...`;
      this.cdr.detectChanges();
      this.zone.runOutsideAngular(() => this.renderBatch(submissions));
    });
  }

  private renderBatch(subs: Submission[]): void {
    this.zone.run(() => { this.batch = subs; this.cdr.detectChanges(); });
    const finish = async () => {
      const wrappers = this.hostElRef.nativeElement.querySelectorAll(
        '.worker-hidden > app-submission-export-view'
      ) as NodeListOf<HTMLElement>;
      wrappers.forEach((el, i) => {
        const sub = subs[i]; if (!sub) { return; }
        const pi = el.querySelector('#paperinfo') as HTMLElement | null;
        if (!pi) { return; }
        const clone = pi.cloneNode(true) as HTMLElement;
        clone.querySelectorAll('[remove-from-export]:not(.status-stamp)').forEach(el => el.remove());
        const html = stripAngularDevAttrs(replaceMatIconsWithSvg(clone.outerHTML));
        this.results.set(sub.id, this.sanitizer.bypassSecurityTrustHtml(html));
      });
      this.zone.run(() => { this.batch = []; this.cdr.detectChanges(); });
      // Constrói o zip via service + dispara download.
      this.generatingLabel = 'Montando arquivo...';
      this.cdr.detectChanges();
      const themeClass = (document.body.className.match(/\b(?:light|dark)-theme\b/) || ['light-theme'])[0] || 'light-theme';
      const sorted = this.all.slice().sort((a, b) => a.id - b.id);
      try {
        const blob = await this.exportPipeline.buildSiteZipBlob({
          sorted, results: this.results, event: this.event, themeClass,
        });
        if (this.destroyed) { return; }
        this.exportService.downloadBlob(this.exportPipeline.siteZipFilename(this.event), blob);
      } catch (e) {
        console.error('[chooser] site-zip build failed', e);
      }
      this.zone.run(() => {
        this.generating = false;
        this.adminService.progressBar?.stop();
        this.cdr.detectChanges();
      });
    };
    let done = false;
    const wrap = () => { if (done) { return; } done = true; sub.unsubscribe(); clearTimeout(safety); finish(); };
    const sub = this.zone.onStable.subscribe(wrap);
    const safety = setTimeout(wrap, 30000);
  }
}
